import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { Receipt, RefreshCw, Ban, Printer } from 'lucide-react';
import { loadPosPrinting, printPosOrder } from '../utils/posPrinter';
import { useAuth } from '../contexts/AuthContext';

const money = (c) => '$' + (Number(c || 0) / 100).toFixed(2);
const today = () => new Date().toISOString().slice(0, 10);
const STATUS = ['', 'open', 'preparing', 'ready', 'completed', 'voided'];
const badge = {
  open: 'bg-gray-600', preparing: 'bg-indigo-700', ready: 'bg-emerald-700',
  completed: 'bg-gray-700 text-gray-400', voided: 'bg-red-900 text-red-300',
};

export default function PosOrders() {
  const { can } = useAuth();
  const canVoid = can('pos:manage');
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState('');
  const [date, setDate] = useState(today());
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [printing, setPrinting] = useState(null);
  const [location, setLocation] = useState(null);
  const [printingId, setPrintingId] = useState(null);

  useEffect(() => {
    loadPosPrinting().then(setPrinting);
    api.get('/pos/location').then((r) => setLocation(r.data.location)).catch(() => {});
  }, []);

  const reprint = async (o, which) => {
    setPrintingId(o.id + which);
    const r = await printPosOrder(o, printing, location, which === 'ticket' ? { kitchen: true } : { receipt: true, reprint: true });
    setPrintingId(null);
    if (r.ok && !r.skipped) toast.success(which === 'ticket' ? 'Kitchen ticket printed' : 'Receipt printed');
    else if (r.skipped) toast('Nothing to print for this order');
    else toast.error(r.error || 'Print failed', { duration: 10000 });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = [];
      if (status) params.push(`status=${status}`);
      if (date) params.push(`businessDate=${date}`);
      const res = await api.get(`/pos/orders${params.length ? '?' + params.join('&') : ''}`);
      setOrders(res.data.orders || []);
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to load orders'); }
    finally { setLoading(false); }
  }, [status, date]);
  useEffect(() => { load(); }, [load]);

  const voidOrder = async (o) => {
    const paid = o.status === 'completed' || o.status === 'preparing' || o.status === 'ready' || o.paidAt;
    const reason = window.prompt(`Void order #${o.orderNumber}?${paid ? ' This refunds the payment (chips back to the wallet, or cash) and restores stock.' : ''} Reason:`);
    if (reason == null) return;
    try { await api.post(`/pos/orders/${o.id}/void`, { reason }); toast.success('Voided'); setOpen(null); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Void failed'); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto text-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Receipt className="w-6 h-6" /> POS Orders</h1>
        <button onClick={load} className="flex items-center gap-2 px-3 py-2 bg-gray-700 rounded-lg hover:bg-gray-600"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
      </div>
      <div className="flex gap-2 mb-4">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2">
          {STATUS.map((s) => <option key={s} value={s}>{s ? s : 'All statuses'}</option>)}
        </select>
      </div>

      <div className="bg-gray-800 rounded-xl divide-y divide-gray-700">
        {orders.map((o) => (
          <div key={o.id}>
            <button onClick={() => setOpen(open === o.id ? null : o.id)} className="w-full flex items-center justify-between p-3 hover:bg-gray-700/50 text-left">
              <div className="flex items-center gap-3">
                <span className="font-bold">#{o.orderNumber}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${badge[o.status] || 'bg-gray-600'}`}>{o.status}</span>
                {o.customerName && <span className="text-sm text-gray-400">{o.customerName}</span>}
                {o.staffName && <span className="text-xs text-gray-500">· {o.staffName}{o.staffNumber ? ` (#${o.staffNumber})` : ''}</span>}
              </div>
              <span className="font-medium">{money(o.totalCents)}</span>
            </button>
            {open === o.id && (
              <div className="px-4 pb-4 text-sm">
                {(o.lines || []).map((l) => (
                  <div key={l.id} className={`flex justify-between ${l.voided ? 'line-through text-gray-500' : ''}`}>
                    <span>{l.qty}× {l.nameSnapshot}{l.notes ? ` (${l.notes})` : ''}</span>
                    <span>{money(l.lineSubtotalCents + l.lineTaxCents)}</span>
                  </div>
                ))}
                <div className="flex justify-between mt-2 pt-2 border-t border-gray-700 text-gray-400"><span>Subtotal</span><span>{money(o.subtotalCents)}</span></div>
                <div className="flex justify-between text-gray-400"><span>Tax</span><span>{money(o.taxCents)}</span></div>
                <div className="flex justify-between font-bold"><span>Total</span><span>{money(o.totalCents)}</span></div>
                {(o.tenders || []).filter((t) => !t.voided).map((t) => (
                  <div key={t.id} className="flex justify-between text-emerald-400"><span>{t.type}{t.changeCents ? ` (change ${money(t.changeCents)})` : ''}</span><span>{money(t.amountCents)}</span></div>
                ))}
                {o.voidReason && <div className="text-red-300 mt-2">Voided: {o.voidReason}</div>}
                {printing?.printerHost && o.status !== 'voided' && (
                  <div className="mt-3 flex gap-2">
                    <button disabled={!!printingId} onClick={() => reprint(o, 'ticket')} className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 rounded-lg text-sm hover:bg-gray-600 disabled:opacity-50"><Printer className="w-4 h-4" /> {printingId === o.id + 'ticket' ? 'Printing...' : 'Print kitchen ticket'}</button>
                    {o.paidAt && (
                      <button disabled={!!printingId} onClick={() => reprint(o, 'receipt')} className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 rounded-lg text-sm hover:bg-gray-600 disabled:opacity-50"><Printer className="w-4 h-4" /> {printingId === o.id + 'receipt' ? 'Printing...' : 'Print receipt'}</button>
                    )}
                  </div>
                )}
                {canVoid && o.status !== 'voided' && (
                  <button onClick={() => voidOrder(o)} className="mt-3 flex items-center gap-2 px-3 py-1.5 bg-red-900 text-red-200 rounded-lg text-sm hover:bg-red-800"><Ban className="w-4 h-4" /> Void order</button>
                )}
              </div>
            )}
          </div>
        ))}
        {!orders.length && !loading && <div className="text-gray-500 text-center py-12">No orders for this filter.</div>}
      </div>
    </div>
  );
}
